import { Media } from '../../components/ui'
import { formatDuration } from '../../utils/format'
import { cn } from '../../utils/cn'
import { ServicePrice } from './ServicePrice'
import type { Service } from '../../types/storefront'

export interface ServiceCardProps {
  service: Service
  onOpen: (service: Service) => void
  className?: string
}

/**
 * The whole card is one button: the Stitch card has no secondary target, and a
 * single control keeps the tap area honest on a phone.
 */
export function ServiceCard({ service, onOpen, className }: ServiceCardProps) {
  return (
    <button
      type="button"
      onClick={() => onOpen(service)}
      className={cn(
        'group flex w-full flex-col overflow-hidden rounded-lg border border-primary/5 bg-surface-container-low text-left transition-colors duration-300 hover:border-primary/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        className,
      )}
    >
      <div className="aspect-[4/5] w-full overflow-hidden bg-surface-container-high">
        <Media
          src={service.image}
          alt={service.name}
          kind="service"
          className="transition-transform duration-700 group-hover:scale-105"
        />
      </div>

      <div className="flex flex-1 flex-col gap-2 p-6">
        {service.category_name && (
          <span className="font-label-caps text-label-caps uppercase text-primary">{service.category_name}</span>
        )}
        <h3 className="font-display text-headline-sm text-on-surface">{service.name}</h3>
        {service.description && (
          <p className="line-clamp-2 font-body-md text-body-md text-on-surface-variant">{service.description}</p>
        )}
        <div className="mt-auto flex items-end justify-between gap-4 pt-4">
          <ServicePrice service={service} />
          <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
            {formatDuration(service.duration)}
          </span>
        </div>
      </div>
    </button>
  )
}
